/**
 * Color index for Stack Rank's tiered UI (E20-6317). Kept as its own file, separate from
 * TieredMetricColors (metricMonthlySummaryCard/GradientDefs.tsx), per explicit instruction -
 * PLACEHOLDER hex values below, to be updated with exact design values later. Nothing in this
 * file affects the legacy Stack Rank chart/grid/ranking colors, which are untouched.
 */

/**
 * TieredAttainmentChart band colors - one entry per metric color index (same index convention
 * used everywhere else: a metric's position among the displayed metrics, mod array length).
 * Three shades per metric family (not the reference/light/dark triple used elsewhere, since this
 * chart's reference asks for three solidly-colored, fairly saturated tones - "darkest/darker/dark"
 * - rather than one pale track color):
 *   belowMeets -> darkest
 *   meets      -> darker
 *   exceeds    -> dark (the brightest/least-dark of the three, but still solid, not pale)
 */
export interface StackRankBandColorSet {
    belowMeets: string;
    meets: string;
    exceeds: string;
}

// Exact hex values (confirmed) - one entry per metric color index, matching the same 6-color
// family ordering used elsewhere in the app (orange/rust, teal, blue, purple, magenta, pink).
export const StackRankBandColors: StackRankBandColorSet[] = [
    { belowMeets: '#B14F2F', meets: '#E26A43', exceeds: '#FF9933' }, // 0 - orange/rust family
    { belowMeets: '#089886', meets: '#00B6B2', exceeds: '#0CD9BF' }, // 1 - teal family
    { belowMeets: '#2B5B93', meets: '#2A78D3', exceeds: '#499DFF' }, // 2 - blue family
    { belowMeets: '#601AA1', meets: '#8913CD', exceeds: '#AC53FF' }, // 3 - purple family
    { belowMeets: '#7D1F78', meets: '#C848C2', exceeds: '#EB6BE5' }, // 4 - magenta family
    { belowMeets: '#C11E52', meets: '#E83683', exceeds: '#FF568B' }, // 5 - pink/rose family
];

/**
 * Fixed 3-color scheme for the Stack Rank grid cells and the grid's selected-row/RankingCard's
 * selected-employee highlight - independent of metric identity (unlike StackRankBandColors
 * above). Below Meets/Exceeds reuse hex already established elsewhere in this app for those
 * exact statuses; Meets (blue) and the selected-row/selected-employee pink are genuinely new to
 * Stack Rank and don't exist anywhere else yet.
 */
export const StackRankGridColors = {
    belowMeets: '#E23636', // exact - matches the red used for "Below Meets" on the main gauge card
    meets: '#1A5AA5', // TODO(design): placeholder - blue not yet defined elsewhere in this app
    exceeds: '#2AA154', // exact - matches the green used for "Exceeds"/"Meets" success states elsewhere
};

// Fixed per-entity identity colors for the tiered chart's dots and legend swatches - these do
// NOT vary by metric or status, unlike StackRankBandColors above. Exact hex values as specified;
// deliberately different from the legacy AttainmentChart.tsx's own rgb() constants, which stay
// untouched.
export const StackRankEntityColors = {
    me: '#7D71E2',
    team: '#1A5AA5',
    scorecard: '#00B6B2',
    topPerformers: '#2AA154',
};

// TODO(design): placeholder - exact "Dark Pink" hex for RankingCard's selected-employee
// background wasn't provided; this is a reasonable dark-pink approximation pending the real value.
export const STACK_RANK_SELECTED_EMPLOYEE_COLOR = '#9C1457';
